/**
 * Safe zones — the inner rectangle of a channel's canvas where content is
 * safe from the platform's own chrome (captions, action rails, progress
 * bars). The bands outside it are the keep-out bands.
 *
 * Stored as four insets in % of the canvas, per channel and per placement
 * where a channel has several; each entry remembers the aspect bucket it was
 * designed for. Universal only (SA / CS edit), resolution: stored universal
 * value → seed below. Mirrors `DEFAULT_UNIVERSAL_SAFE_ZONES` in
 * `api_gateway/models/tendv2_settings.py`.
 *
 * Set elements stop at the safe edge; design elements do not (they may go
 * to the image edge, or past it when rotated).
 */
import { AspectBucket, CHANNEL_PLATFORMS, resolveChannelAspect } from './channelAspect';

/** A box in canvas px (the safe rectangle, an element's bounds). */
export interface Rect { left: number; top: number; width: number; height: number }

export interface SafeZoneInsets {
  /** % of canvas height */
  top: number;
  /** % of canvas height */
  bottom: number;
  /** % of canvas width */
  left: number;
  /** % of canvas width */
  right: number;
}

export interface SafeZoneEntry extends SafeZoneInsets {
  bucket: AspectBucket;
}

/** channel → placement (or 'default') → insets */
export type SafeZoneMap = Record<string, Record<string, SafeZoneEntry>>;

/** Today's row inset: 16 px at the 360 px reference width. */
export const DEFAULT_INSET_PCT = 4.4;

const plain = (bucket: AspectBucket, pct = DEFAULT_INSET_PCT): SafeZoneEntry =>
  ({ top: pct, bottom: pct, left: pct, right: pct, bucket });

/** Seeds from the 2026 platform guides (see the Safe Zones ticket for sources). */
export const DEFAULT_UNIVERSAL_SAFE_ZONES: SafeZoneMap = {
  tiktok: { default: { top: 7, bottom: 25, left: 4, right: 13, bucket: '9:16' } },
  instagram: {
    default: plain('4:5'),
    feed: plain('4:5'),
    reel: { top: 13, bottom: 22, left: 5, right: 11, bucket: '9:16' },
    reel_off_grid: { top: 13, bottom: 22, left: 5, right: 11, bucket: '9:16' },
    story: { top: 13, bottom: 13, left: 5, right: 5, bucket: '9:16' },
  },
  facebook: {
    default: plain('4:5'),
    feed: plain('4:5'),
    reel: { top: 14, bottom: 35, left: 6, right: 6, bucket: '9:16' },
    story: { top: 13, bottom: 20, left: 5, right: 5, bucket: '9:16' },
  },
  youtube_shorts: { default: { top: 9, bottom: 23, left: 4, right: 15, bucket: '9:16' } },
  snapchat: {
    default: { top: 8, bottom: 8, left: 5, right: 5, bucket: '9:16' },
    story: { top: 8, bottom: 8, left: 5, right: 5, bucket: '9:16' },
    saved_story: { top: 8, bottom: 8, left: 5, right: 5, bucket: '9:16' },
    spotlight: { top: 8, bottom: 20, left: 5, right: 12, bucket: '9:16' },
  },
  pinterest: { default: plain('2:3') },
  twitter: { default: plain('1:1') },
  bluesky: { default: plain('1:1') },
  reddit: { default: plain('1:1') },
  quora: { default: plain('1:1') },
  linkedin: { default: plain('4:5') },
  threads: { default: plain('4:5') },
  newsletter: { default: plain('16:9') },
};

const normChannel = (channel: string): string => {
  const c = (channel || '').toLowerCase();
  return c === 'youtube' ? 'youtube_shorts' : c;
};

const isInsets = (v: unknown): v is SafeZoneInsets =>
  !!v && typeof v === 'object' &&
  ['top', 'bottom', 'left', 'right'].every((k) => typeof (v as Record<string, unknown>)[k] === 'number');

/** Stored universal value merged over the seeds; junk entries are ignored. */
export function mergeSafeZones(stored: unknown): SafeZoneMap {
  const out: SafeZoneMap = {};
  for (const [ch, placements] of Object.entries(DEFAULT_UNIVERSAL_SAFE_ZONES)) {
    out[ch] = { ...placements };
  }
  if (stored && typeof stored === 'object') {
    for (const [ch, placements] of Object.entries(stored as Record<string, unknown>)) {
      if (!placements || typeof placements !== 'object') continue;
      const channel = normChannel(ch);
      out[channel] = { ...(out[channel] ?? {}) };
      for (const [pl, entry] of Object.entries(placements as Record<string, unknown>)) {
        if (!isInsets(entry)) continue;
        const seedBucket = out[channel][pl]?.bucket ?? out[channel].default?.bucket ?? '1:1';
        const bucket = ((entry as SafeZoneEntry).bucket ?? seedBucket) as AspectBucket;
        out[channel][pl] = { top: entry.top, bottom: entry.bottom, left: entry.left, right: entry.right, bucket };
      }
    }
  }
  return out;
}

/** The zone for a channel + placement: placement entry → channel default → plain inset. */
export function resolveSafeZone(
  universal: SafeZoneMap | null | undefined,
  channel: string,
  placement?: string | null,
): SafeZoneEntry {
  const map = universal ?? DEFAULT_UNIVERSAL_SAFE_ZONES;
  const ch = map[normChannel(channel)] ?? DEFAULT_UNIVERSAL_SAFE_ZONES[normChannel(channel)];
  if (!ch) return plain('1:1');
  if (placement && ch[placement]) return ch[placement];
  return ch.default ?? Object.values(ch)[0] ?? plain('1:1');
}

/**
 * For a template with no channel: the strictest inset on each side across
 * every channel (and placement) whose canvas is this bucket, so a template
 * that clears it is safe on all of them.
 */
export function strictestSafeZoneForBucket(
  universal: SafeZoneMap | null | undefined,
  universalAspect: Record<string, string> | null | undefined,
  bucket: AspectBucket,
): SafeZoneEntry {
  const map = universal ?? DEFAULT_UNIVERSAL_SAFE_ZONES;
  const out: SafeZoneEntry = { top: 0, bottom: 0, left: 0, right: 0, bucket };
  let any = false;
  for (const channel of CHANNEL_PLATFORMS) {
    const entries = Object.values(map[channel] ?? {});
    for (const e of entries) {
      // A placement entry designed for this bucket counts; a channel whose
      // resolved canvas is this bucket counts through its default entry.
      const channelBucket = resolveChannelAspect(null, universalAspect, channel);
      if (e.bucket !== bucket && !(channelBucket === bucket && e === map[channel]?.default)) continue;
      any = true;
      out.top = Math.max(out.top, e.top);
      out.bottom = Math.max(out.bottom, e.bottom);
      out.left = Math.max(out.left, e.left);
      out.right = Math.max(out.right, e.right);
    }
  }
  return any ? out : plain(bucket);
}

/** The safe rectangle in canvas px. */
export function safeRectPx(insets: SafeZoneInsets, canvasW: number, canvasH: number): Rect {
  const left = (insets.left / 100) * canvasW;
  const top = (insets.top / 100) * canvasH;
  const right = (insets.right / 100) * canvasW;
  const bottom = (insets.bottom / 100) * canvasH;
  return { left, top, width: Math.max(0, canvasW - left - right), height: Math.max(0, canvasH - top - bottom) };
}

/** Clamp an inset edit to something sane (each band at most 45 %). */
export const clampInset = (v: number) => Math.max(0, Math.min(45, Math.round(v * 10) / 10));

/** Today's inset on every side — what a canvas without a channel anchors to. */
export const PLAIN_SAFE_ZONE: SafeZoneInsets = {
  top: DEFAULT_INSET_PCT, bottom: DEFAULT_INSET_PCT, left: DEFAULT_INSET_PCT, right: DEFAULT_INSET_PCT,
};
